import {
  useQuery,
  QueryClient,
  QueryClientProvider as QueryClientProviderBase,
} from '@tanstack/react-query';
import supabase from './supabase';
import { formatDateToYYYYMMDD, formatSearchStringForFts } from './data-factory';

// React Query client
const client = new QueryClient();

/**** USERS ****/

// Fetch user data
// Note: This is called automatically in `auth.js` and data is merged into `auth.user`
export function useUser(uid) {
  // Manage data fetching with React Query: https://react-query.tanstack.com/overview
  return useQuery({
    // Unique query key: https://react-query.tanstack.com/guides/query-keys
    queryKey: ['user', { uid }],
    // Query function that fetches data
    queryFn: () =>
      supabase.from('users').select(`*, customers ( * )`).eq('id', uid).single().then(handle),
    // Only call query function if we have a `uid`
    enabled: !!uid,
  });
}

// Fetch user data (non-hook)
// Useful if you need to fetch data from outside of a component
export function getUser(uid) {
  return supabase.from('users').select(`*, customers ( * )`).eq('id', uid).single().then(handle);
}

// Update an existing user
export async function updateUser(uid, data) {
  const response = await supabase.from('users').update(data).eq('id', uid).then(handle);
  // Invalidate and refetch queries that could have old data
  await client.invalidateQueries(['user', { uid }]);
  return response;
}

/**** ITEMS ****/
/* Example query functions (modify to your needs) */

// Fetch item data
export function useItem(id) {
  return useQuery({
    queryKey: ['item', { id }],
    queryFn: () => supabase.from('items').select().eq('id', id).single().then(handle),
    enabled: !!id,
  });
}

export function useItemsByOwner(owner) {
  return useQuery({
    queryKey: ['items', { owner }],
    queryFn: () =>
      supabase
        .from('items')
        .select()
        .eq('owner', owner)
        .order('createdAt', { ascending: false })
        .then(handle),
    enabled: !!owner,
  });
}

// Create a new item
export async function createItem(data) {
  const response = await supabase.from('items').insert([data]).then(handle);
  // Invalidate and refetch queries that could have old data
  await client.invalidateQueries(['items']);
  return response;
}

// Update an item
export async function updateItem(id, data) {
  const response = await supabase.from('items').update(data).eq('id', id).then(handle);
  // Invalidate and refetch queries that could have old data
  await Promise.all([
    client.invalidateQueries(['item', { id }]),
    client.invalidateQueries(['items']),
  ]);
  return response;
}

// Delete an item
export async function deleteItem(id) {
  const response = await supabase.from('items').delete().eq('id', id).then(handle);
  // Invalidate and refetch queries that could have old data
  await Promise.all([
    client.invalidateQueries(['item', { id }]),
    client.invalidateQueries(['items']),
  ]);
  return response;
}

/**** HELPERS ****/

// Get response data or throw error if there is one
function handle(response) {
  if (response.error) throw response.error;
  return response.data;
}

// React Query context provider that wraps our app
export function QueryClientProvider(props) {
  return <QueryClientProviderBase client={client}>{props.children}</QueryClientProviderBase>;
}

// Create a list item in the lists table.
export async function createList({ owner, list_name, shows, is_private }) {
  const { data, error } = await supabase
    .from('saved_lists')
    .insert([{ owner, list_name, shows, is_private }]);

  if (error) {
    throw error;
  }

  return data;
}

// Delete a list by its id.
export async function deleteList(listId) {
  const { data, error } = await supabase.from('saved_lists').delete().eq('id', listId);

  if (error) {
    throw error;
  }

  return data;
}

// Add a show to a list by its id.
export async function addShowToList(listId, showId, shows) {
  const { data: currentData, error: fetchError } = await supabase
    .from('saved_lists')
    .select('shows')
    .eq('id', listId)
    .single();

  if (fetchError) {
    throw fetchError;
  }

  const currentShows = currentData.shows || [];

  // Check if any of the shows to add already exists in the list
  const showsToAddFiltered = shows.filter(show => !currentShows.includes(show));

  // If no new shows to add, remove the specified showId from the list
  const updatedShows =
    showsToAddFiltered.length === 0
      ? currentShows.filter(show => show !== showId)
      : [...currentShows, ...showsToAddFiltered];

  const { data: updatedData, error: updateError } = await supabase
    .from('saved_lists')
    .update({ shows: updatedShows })
    .eq('id', listId);

  if (updateError) {
    throw updateError;
  }

  return updatedData;
}

/** Toggle whether the user has marked a show as seen */
export async function toggleSeenShow(userId, showId) {
  const { data: existing, error: fetchError } = await supabase
    .from('seen_shows')
    .select('id')
    .eq('user_id', userId)
    .eq('show_id', showId)
    .maybeSingle();

  if (fetchError) {
    throw fetchError;
  }

  if (existing) {
    const { error } = await supabase
      .from('seen_shows')
      .delete()
      .eq('user_id', userId)
      .eq('show_id', showId);

    if (error) {
      throw error;
    }
    return { removed: true };
  }

  const { error } = await supabase
    .from('seen_shows')
    .insert([{ user_id: userId, show_id: showId }]);

  if (error) {
    throw error;
  }
  return { added: true };
}

export async function getWantToSeeIdsByUser(userId) {
  const { data, error } = await supabase
    .from('want_to_see')
    .select('show_id')
    .eq('user_id', userId);
  if (error) throw error;
  return new Set((data ?? []).map(row => row.show_id));
}

export async function addShowToWantToSee(userId, showId) {
  const { data: existing } = await supabase
    .from('want_to_see')
    .select('id')
    .eq('user_id', userId)
    .eq('show_id', showId)
    .maybeSingle();
  if (existing) return { added: false };

  const { error } = await supabase
    .from('want_to_see')
    .insert([{ user_id: userId, show_id: showId }]);
  if (error) throw error;
  return { added: true };
}

export async function removeShowFromWantToSee(userId, showId) {
  const { error } = await supabase
    .from('want_to_see')
    .delete()
    .eq('user_id', userId)
    .eq('show_id', showId);
  if (error) throw error;
}

export async function addSeenShowNote(userId, showId, notes) {
  const { error } = await supabase
    .from('seen_shows')
    .update({ notes })
    .eq('user_id', userId)
    .eq('show_id', showId);
  if (error) throw error;
}

/** All show ids the user has marked as seen. */
export async function getSeenShowIdsForUser(userId) {
  const { data, error } = await supabase.from('seen_shows').select('show_id').eq('user_id', userId);

  if (error) {
    throw error;
  }
  return new Set((data ?? []).map(row => row.show_id));
}

/** Lightweight rows for heatmap (all `created_at` values for a user). */
export async function getSeenShowRowsForHeatmap(userId) {
  const { data, error } = await supabase
    .from('seen_shows')
    .select('created_at')
    .eq('user_id', userId);

  if (error) {
    throw error;
  }
  return data ?? [];
}

/**
 * Paginated seen shows with full show + venue data
 */
export async function getSeenShowsDataByUserPaginated(userId, limit = 20, offset = 0) {
  const { data: seenShows, error: seenShowsError } = await supabase
    .from('seen_shows')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (seenShowsError) {
    throw seenShowsError;
  }
  if (!seenShows || seenShows.length === 0) {
    return [];
  }

  const showIds = seenShows.map(s => s.show_id);

  const { data: showsData, error } = await supabase
    .from('shows')
    .select(
      '*, venues!inner(neighborhood, venue_name, city, address1, phone, latitude, longitude, image_urls )'
    )
    .in('show_id', showIds);

  if (error) {
    throw error;
  }

  const seenShowsMap = {};
  seenShows.forEach(s => {
    seenShowsMap[s.show_id] = {
      created_at: s.created_at,
      notes: s.notes || null,
    };
  });

  const merged = showsData.map(show => ({
    ...show,
    created_at: seenShowsMap[show.show_id]?.created_at,
    notes: seenShowsMap[show.show_id]?.notes,
  }));

  return merged.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

/**
 * User profiles who marked this show as seen
 */
export async function getShowSeenByUsers(showId) {
  const { data, error } = await supabase
    .from('seen_shows')
    .select('users!inner(id, username, picture, name)')
    .eq('show_id', showId);

  if (error) {
    throw error;
  }

  const users = (data ?? [])
    .map(item => {
      const u = item.users;
      return Array.isArray(u) ? u : u ? [u] : [];
    })
    .flat()
    .map(user => ({
      id: String(user.id),
      username: String(user.username ?? ''),
      name: user.name ?? null,
      picture:
        user.picture != null && String(user.picture).trim() ? String(user.picture).trim() : null,
    }));

  return users.filter((user, index, self) => index === self.findIndex(u => u.id === user.id));
}

/**
 * Fetch rows from a show-scoped analytics table for a set of show IDs from a given date forward.
 * @param {string} table
 * @param {string} columns
 * @param {string} dateColumn
 * @param {number[]} showIds
 * @param {Date} fromDate
 * @returns {Promise<object[]>}
 */
async function getShowAnalyticsRows(table, columns, dateColumn, showIds, fromDate) {
  if (!showIds || showIds.length === 0) return [];

  const { data, error } = await supabase
    .from(table)
    .select(columns)
    .in('show_id', showIds)
    .gte(dateColumn, fromDate.toISOString());

  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch raw show_views rows for a set of show IDs from a given date forward.
 * @param {number[]} showIds
 * @param {Date} fromDate
 * @returns {Promise<{ show_id: number, created_at: string }[]>}
 */
export async function getShowViewsByShowIds(showIds, fromDate) {
  return getShowAnalyticsRows('show_views', 'show_id, created_at', 'created_at', showIds, fromDate);
}

/**
 * Fetch raw seen_shows rows for a set of show IDs from a given date forward.
 * @param {number[]} showIds
 * @param {Date} fromDate
 * @returns {Promise<{ show_id: number, created_at: string }[]>}
 */
export async function getSeenShowsByShowIds(showIds, fromDate) {
  return getShowAnalyticsRows('seen_shows', 'show_id, created_at', 'created_at', showIds, fromDate);
}

/**
 * Fetch pre-aggregated show_impressions rows for a set of show IDs from a given date forward.
 * @param {number[]} showIds
 * @param {Date} fromDate
 * @returns {Promise<{ show_id: number, view_date: string, platform: string, source: string, count: number }[]>}
 */
export async function getShowImpressionsByShowIds(showIds, fromDate) {
  return getShowAnalyticsRows('show_impressions', 'show_id, view_date, platform, source, count', 'view_date', showIds, fromDate);
}

export async function removeShowFromList(listId, showId) {
  const { data: currentData, error: fetchError } = await supabase
    .from('saved_lists')
    .select('shows')
    .eq('id', listId)
    .single();

  if (fetchError) {
    throw fetchError;
  }

  const currentShows = currentData.shows || [];
  const updatedShows = currentShows.filter(show => show !== showId);

  const { data: updatedData, error: updateError } = await supabase
    .from('saved_lists')
    .update({ shows: updatedShows })
    .eq('id', listId);

  if (updateError) {
    throw updateError;
  }

  return updatedData;
}

const NOT_ARCHIVED_FILTER = 'is_archived.is.null,is_archived.eq.false';
const SHOW_VENUE_SELECT =
  '*, venues!inner(venue_id, org_id, neighborhood, venue_name, city, address1, phone, latitude, longitude, image_urls)';

const isVenueArchived = venue => venue?.is_archived === true || venue?.isArchived === true;

export async function getOrgDataByID(orgId) {
  const { data, error } = await supabase
    .from('organization')
    .select('org_id, org_name, bio')
    .eq('org_id', Number(orgId))
    .single();
  if (error) throw error;
  return data;
}

export async function getPublicVenuesByOrgID(orgId) {
  const { data, error } = await supabase.from('venues').select('*').eq('org_id', Number(orgId));
  if (error) throw error;
  return (data ?? []).filter(venue => !isVenueArchived(venue));
}

export async function getPublicShowsByVenueID(venueId) {
  const { data, error } = await supabase
    .from('shows')
    .select(SHOW_VENUE_SELECT)
    .eq('venue_id', venueId)
    .eq('duplicate', 0)
    .or(NOT_ARCHIVED_FILTER);
  if (error) throw error;
  return data ?? [];
}

export async function getOrgPageData(orgId) {
  const [org, venues] = await Promise.all([getOrgDataByID(orgId), getPublicVenuesByOrgID(orgId)]);
  const showsPerVenue = await Promise.all(venues.map(v => getPublicShowsByVenueID(v.venue_id)));
  const shows = showsPerVenue.flat().filter(show => !show.is_archived);

  const today = formatDateToYYYYMMDD(new Date());
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  const liveShows = shows
    .filter(show => !show.end_date || show.end_date >= today)
    .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
  const pastShows = shows
    .filter(show => show.end_date && show.end_date < today && new Date(show.end_date) >= oneYearAgo)
    .sort((a, b) => new Date(b.end_date) - new Date(a.end_date));

  return { org, venues, liveShows, pastShows };
}

export async function getIsFollowing(userId, orgId) {
  const { data, error } = await supabase
    .from('following_orgs')
    .select('id')
    .eq('user_id', userId)
    .eq('org_id', Number(orgId))
    .maybeSingle();
  if (error) throw error;
  return !!data;
}

export async function toggleIsFollowing(userId, orgId) {
  const following = await getIsFollowing(userId, orgId);
  if (following) {
    const { error } = await supabase
      .from('following_orgs')
      .delete()
      .eq('user_id', userId)
      .eq('org_id', Number(orgId));
    if (error) throw error;
    return { removed: true };
  }
  const { error } = await supabase
    .from('following_orgs')
    .insert([{ user_id: userId, org_id: Number(orgId) }]);
  if (error) throw error;
  return { added: true };
}

export const HIDDEN_SHOW_ID = 9000000;

export const SearchCategory = {
  SHOW: 'show',
  ARTIST: 'artist',
  VENUE: 'venue',
  COMMUNITY: 'community',
};

export async function fullTextSearch(text, searchCategory) {
  if (!text) return [];

  const rpcByCategory = {
    [SearchCategory.SHOW]: 'search_shows_by_title_prefix',
    [SearchCategory.ARTIST]: 'search_shows_by_artist',
    [SearchCategory.VENUE]: 'search_orgs_by_prefix',
    [SearchCategory.COMMUNITY]: 'search_profiles_by_username_prefix',
  };
  const rpcName = rpcByCategory[searchCategory] ?? rpcByCategory[SearchCategory.SHOW];

  const prefix = searchCategory === SearchCategory.ARTIST ? text : formatSearchStringForFts(text);

  const { data, error } = await supabase.rpc(rpcName, { prefix });
  if (error) throw error;

  if (searchCategory === SearchCategory.SHOW || searchCategory === SearchCategory.ARTIST) {
    return (data ?? [])
      .filter(row => row.show_id !== HIDDEN_SHOW_ID && (row.duplicate == null || row.duplicate === 0))
      .map(row => ({
        ...row,
        venues: {
          address1: row.address1,
          latitude: row.latitude,
          longitude: row.longitude,
          neighborhood: row.neighborhood,
          venue_name: row.venue_name,
          city: row.city,
          phone: row.phone,
        },
      }));
  }

  return data ?? [];
}

export async function getNeighborhoodsByOrgIds(orgs) {
  if (!orgs?.length) return [];

  const orgIds = orgs.map(org => Number(org.org_id)).filter(Boolean);
  if (!orgIds.length) return orgs;

  const { data: venues, error } = await supabase
    .from('venues')
    .select('org_id, neighborhood, is_archived')
    .in('org_id', orgIds);
  if (error) throw error;

  const neighborhoodsByOrg = groupNeighborhoods(venues);

  return orgs
    .map(org => ({
      ...org,
      neighborhoods: Array.from(neighborhoodsByOrg.get(Number(org.org_id)) ?? []),
    }))
    .sort((a, b) => {
      const aHas = a.neighborhoods.length > 0;
      const bHas = b.neighborhoods.length > 0;
      if (aHas !== bHas) return aHas ? -1 : 1;
      return (a.org_name || '').localeCompare(b.org_name || '');
    });
}

function groupNeighborhoods(venues) {
  const map = new Map();
  (venues ?? []).forEach(v => {
    if (!v?.org_id || isVenueArchived(v)) return;
    if (!map.has(v.org_id)) map.set(v.org_id, new Set());
    if (v.neighborhood && v.neighborhood.trim()) map.get(v.org_id).add(v.neighborhood);
  });
  return map;
}

const FEATURED_SELECT =
  '*, venues!inner(neighborhood, venue_name, latitude, longitude, address1)';

function featuredBaseQuery(city) {
  return supabase
    .from('shows')
    .select(FEATURED_SELECT)
    .eq('venues.metro_area', city)
    .eq('duplicate', 0)
    .or(NOT_ARCHIVED_FILTER)
    .gte('end_date', formatDateToYYYYMMDD(new Date()));
}

async function runFeaturedQuery(query) {
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).filter(show => show.show_id !== HIDDEN_SHOW_ID);
}

export function getOpeningSoonShows(city = 'New York') {
  return runFeaturedQuery(
    featuredBaseQuery(city)
      .gte('start_date', formatDateToYYYYMMDD(new Date()))
      .order('opening_sort', { ascending: true })
      .limit(10)
  );
}

export function getClosingSoonShows(city = 'New York') {
  const twoWeeksFromNow = formatDateToYYYYMMDD(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));
  return runFeaturedQuery(
    featuredBaseQuery(city)
      .lte('end_date', twoWeeksFromNow)
      .order('closing_sort', { ascending: true })
      .limit(10)
  );
}

export function getHighestRelevanceShows(city = 'New York') {
  return runFeaturedQuery(featuredBaseQuery(city).order('show_relevance', { ascending: false }).limit(10));
}

export async function getMostSeenShows() {
  const { data, error } = await supabase.rpc('get_top_seen_shows_with_count', { limit_count: 10 });
  if (error) throw error;
  return (data ?? []).map(row => ({
    ...row,
    venues: row.venues ?? {
      venue_name: row.venue_name,
      neighborhood: row.neighborhood,
      latitude: row.latitude,
      longitude: row.longitude,
    },
  }));
}

async function getFollowedOrgIds(userId) {
  const { data, error } = await supabase
    .from('following_orgs')
    .select('org_id')
    .eq('user_id', userId);
  if (error) throw error;
  return (data ?? []).map(row => row.org_id);
}

export async function getFollowingOrgsByUser(userId) {
  const orgIds = await getFollowedOrgIds(userId);
  if (!orgIds.length) return [];

  const [{ data: orgs, error: orgsError }, { data: venues, error: venuesError }] =
    await Promise.all([
      supabase.from('organization').select('org_id, org_name, bio').in('org_id', orgIds),
      supabase.from('venues').select('org_id, neighborhood, is_archived').in('org_id', orgIds),
    ]);
  if (orgsError) throw orgsError;
  if (venuesError) throw venuesError;

  const neighborhoodsByOrg = groupNeighborhoods(venues);
  return (orgs ?? [])
    .map(org => ({ ...org, neighborhoods: Array.from(neighborhoodsByOrg.get(org.org_id) ?? []) }))
    .sort((a, b) => (a.org_name || '').localeCompare(b.org_name || ''));
}

export async function getFollowingShowsDataByUser(userId) {
  const orgIds = await getFollowedOrgIds(userId);
  if (!orgIds.length) return [];

  const { data: venues, error: venuesError } = await supabase
    .from('venues')
    .select('venue_id')
    .in('org_id', orgIds);
  if (venuesError) throw venuesError;
  if (!venues?.length) return [];

  const now = new Date().toISOString();
  const inFourWeeksDate = new Date();
  inFourWeeksDate.setDate(inFourWeeksDate.getDate() + 28);
  const inFourWeeks = inFourWeeksDate.toISOString();

  const { data, error } = await supabase
    .from('shows')
    .select(SHOW_VENUE_SELECT)
    .in(
      'venue_id',
      venues.map(v => v.venue_id)
    )
    .or(
      `and(start_date.lte.${now},end_date.gte.${now}),and(start_date.gt.${now},start_date.lte.${inFourWeeks})`
    )
    .or(NOT_ARCHIVED_FILTER);
  if (error) throw error;

  return (data ?? []).sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
}

export async function getWantToSeeDataByUser(userId) {
  const [{ data: rows, error: rowsError }, { data: seenRows, error: seenError }] =
    await Promise.all([
      supabase.from('want_to_see').select('show_id, created_at').eq('user_id', userId),
      supabase.from('seen_shows').select('show_id').eq('user_id', userId),
    ]);
  if (rowsError) throw rowsError;
  if (seenError) throw seenError;
  if (!rows?.length) return [];

  const seenIds = new Set((seenRows ?? []).map(r => r.show_id));
  const savedAt = Object.fromEntries(rows.map(r => [r.show_id, r.created_at]));

  const { data: shows, error } = await supabase
    .from('shows')
    .select(SHOW_VENUE_SELECT)
    .in(
      'show_id',
      rows.map(r => r.show_id)
    );
  if (error) throw error;

  const today = formatDateToYYYYMMDD(new Date());
  return (shows ?? [])
    .filter(show => (!show.end_date || show.end_date >= today) && !seenIds.has(show.show_id))
    .map(show => ({ ...show, created_at: savedAt[show.show_id] }))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

export async function getSeenShowsCountByUser(userId) {
  const { count, error } = await supabase
    .from('seen_shows')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId);
  if (error) throw error;
  return count ?? 0;
}
